import { IRichContentEditorState, IUser, UnsubscribeTemplateViewModel } from '@ViewModels';
import { css } from 'aphrodite';
import { makeObservable, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import {
	ErrorMessagesViewModelKey,
	IErrorMessageComponentProps,
	IUserSessionComponentProps,
	UserSessionViewModelKey,
} from '../../../../../models/AppState';
import { IEventLoggingComponentProps, withEventLogging } from '../../../../../models/Logging';
import { convertRawRichTextContentStateToRichContentEditorState } from '../../../../../models/UiUtils';
import { baseStyleSheet } from '../../../../styles/styles';
import { UnsubscribeSignature } from '../../../UnsubscribeSignature';
import { EmailSignatureComposer } from './presentation';
import { styleSheet } from './styles';

interface IProps extends IErrorMessageComponentProps, IUserSessionComponentProps, IEventLoggingComponentProps {
	forUser?: IUser;
	onCancel?: () => void;
}

interface IState {
	unsubscribeContentEditorState?: IRichContentEditorState;
}

class _EmailSignatureSettings extends React.Component<IProps, IState> {
	private mUnsubscribeTemplate: UnsubscribeTemplateViewModel;
	constructor(props: IProps) {
		super(props);

		makeObservable<_EmailSignatureSettings, 'mUnsubscribeTemplate'>(this, {
			mUnsubscribeTemplate: observable.ref,
		});

		this.mUnsubscribeTemplate = new UnsubscribeTemplateViewModel(props.userSession);
		this.state = {
			unsubscribeContentEditorState: convertRawRichTextContentStateToRichContentEditorState({
				document: '',
			}),
		};
	}

	public async componentDidMount() {
		const { logApiError, logEvent } = this.props;

		logEvent('LoadUnsubscribeTemplate');
		try {
			await this.mUnsubscribeTemplate.loadAccountTemplate();
			if (this.mUnsubscribeTemplate.accountTemplate) {
				await this.mUnsubscribeTemplate.loadUserTemplate();
				const content = this.mUnsubscribeTemplate.userTemplate
					? this.mUnsubscribeTemplate.userTemplate.content
					: this.mUnsubscribeTemplate.accountTemplate?.content;
				this.setState({
					unsubscribeContentEditorState: convertRawRichTextContentStateToRichContentEditorState(content),
				});
			}
		} catch (error) {
			logApiError('LoadUnsubscribeTemplate-Error', error);
		}
	}

	public render() {
		const { forUser } = this.props;
		const { unsubscribeContentEditorState } = this.state;

		const shouldRenderUnsubscribeSignature =
			(!!this.mUnsubscribeTemplate.accountTemplate || !!this.mUnsubscribeTemplate.userTemplate) && !forUser;
		return (
			<>
				<EmailSignatureComposer forUser={forUser} />
				{shouldRenderUnsubscribeSignature ? (
					<div>
						<UnsubscribeSignature
							contentEditorState={unsubscribeContentEditorState}
							onContentEditorStateChanged={this.onContentEditorStateChanged}
						/>
						<div className={css(styleSheet.buttonContainer)}>
							<button
								className={css(baseStyleSheet.ctaButton)}
								disabled={this.mUnsubscribeTemplate.isBusy}
								onClick={this.onSaveUnsubscribeSignatureClicked}
							>
								Save
							</button>
							<button
								className={css(baseStyleSheet.ctaButtonReverse)}
								disabled={this.mUnsubscribeTemplate.isBusy}
								onClick={this.onCancelUnsubscribeSignatureClicked}
							>
								Cancel
							</button>
						</div>
					</div>
				) : null}
			</>
		);
	}

	private onContentEditorStateChanged = (unsubscribeContentEditorState: IRichContentEditorState) => {
		this.setState({ unsubscribeContentEditorState });
	};

	private onSaveUnsubscribeSignatureClicked = () => {
		const { logApiError, logEvent } = this.props;
		const { unsubscribeContentEditorState } = this.state;

		const content = unsubscribeContentEditorState.getRawRichTextContent();

		logEvent('SaveUnsubscribeTemplate');
		this.mUnsubscribeTemplate
			.saveTemplate(content)
			?.then(() => {
				toast.success('Unsubscribe Signature saved');
			})
			?.catch(error => {
				logApiError('SaveUnsubscribeTemplate-Error', error);

				toast.error('Error saving Unsubscribe Signature');
			});
	};

	private onCancelUnsubscribeSignatureClicked = () => {
		const content = this.mUnsubscribeTemplate.userTemplate
			? this.mUnsubscribeTemplate.userTemplate.content
			: this.mUnsubscribeTemplate.accountTemplate.content;
		this.setState({
			unsubscribeContentEditorState: convertRawRichTextContentStateToRichContentEditorState(content),
		});
		// Close the accordion when Cancel is clicked
		this.props.onCancel?.();
	};
}

const EmailSignatureSettingsAsObserver = observer(_EmailSignatureSettings);
const EmailSignatureSettingsWithContext = inject(
	ErrorMessagesViewModelKey,
	UserSessionViewModelKey
)(EmailSignatureSettingsAsObserver);
export const EmailSignatureSettings = withEventLogging(EmailSignatureSettingsWithContext, 'EmailSignatureSettings');
